/**
 * Contrato de classificação do onboarding trial (SOC-4875 / SOC-4873).
 *
 * Mapa onboarding_step (API) — pós-SOC-4875:
 * | step | significado                                      |
 * |------|--------------------------------------------------|
 * | 0/null | ainda não respondeu a 1ª tela                  |
 * | 1    | escolheu operação (ramo intermediário)           |
 * | 2    | respondeu escopo franquia (UMA/VÁRIAS)           |
 * | 3    | classificação completa (tipo+faixa) → vai p/ WA   |
 * | 4    | etapa de WhatsApp (ContactCard)                  |
 * | 5+   | onboarding concluído (redirect app)             |
 *
 * Contas legadas (pré-SOC-4875) NÃO são migradas: seus onboarding_step
 * devem ser tratados como opacos/legado no Admin — não reinterpretar.
 *
 * Keys type_descrition alinhadas ao Admin / AboutYou:
 * Entrepreneur | Agency_Or_Profissional | Franchisee | Franchising | Outros
 */

export const ACCOUNT_TYPES = {
  ENTREPRENEUR: 'Entrepreneur',
  AGENCY_OR_PROFESSIONAL: 'Agency_Or_Profissional',
  FRANCHISEE: 'Franchisee',
  FRANCHISING: 'Franchising',
  OUTROS: 'Outros',
};

export const ONBOARDING_STEPS = {
  NOT_STARTED: 0,
  OPERATION_CHOICE: 1,
  FRANCHISE_SCOPE: 2,
  OPERATION_COMPLETE: 3,
  CONTACT: 4,
  COMPLETED: 5,
};

export const UI_SCREENS = {
  OPERATION: 'operation',
  AGENCY_SCALE: 'agency_scale',
  FRANCHISE_SCOPE: 'franchise_scope',
  FRANCHISE_SCALE: 'franchise_scale',
  OTHER_SCALE: 'other_scale',
  CONTACT: 'contact',
};

export const OPERATION_OPTIONS = {
  FEW_NETWORKS: 'few_networks',
  AGENCY: 'agency',
  FRANCHISE: 'franchise',
  OTHER: 'other',
};

export const RANGE_KEYS = {
  ONE: '1',
  TWO_TO_NINE: '2-9',
  TEN_TO_THIRTY: '10-30',
  THIRTY_PLUS: '30+',
  TEN_TO_TWENTY_NINE: '10-29',
  THIRTY_TO_NINETY_NINE: '30-99',
  HUNDRED_PLUS: '100+',
};

const RANGE_TO_COUNT = {
  [RANGE_KEYS.ONE]: 1,
  [RANGE_KEYS.TWO_TO_NINE]: 9,
  [RANGE_KEYS.TEN_TO_THIRTY]: 30,
  [RANGE_KEYS.THIRTY_PLUS]: 30,
  [RANGE_KEYS.TEN_TO_TWENTY_NINE]: 29,
  [RANGE_KEYS.THIRTY_TO_NINETY_NINE]: 99,
  [RANGE_KEYS.HUNDRED_PLUS]: 100,
};

/** Agency/Other: 1 / 9 / 29 / 30 (SOC-4873 — evita colisão 10-30 e 30+ → 30). */
export const CLIENTS_OR_NETWORKS_RANGES = [
  RANGE_KEYS.ONE,
  RANGE_KEYS.TWO_TO_NINE,
  RANGE_KEYS.TEN_TO_TWENTY_NINE,
  RANGE_KEYS.THIRTY_PLUS,
];

export const FRANCHISE_UNITS_RANGES = [
  RANGE_KEYS.TWO_TO_NINE,
  RANGE_KEYS.TEN_TO_TWENTY_NINE,
  RANGE_KEYS.THIRTY_TO_NINETY_NINE,
  RANGE_KEYS.HUNDRED_PLUS,
];

export const mapRangeToCount = (rangeKey) => RANGE_TO_COUNT[rangeKey];

export const resolveOperationAnswer = (optionKey) => {
  switch (optionKey) {
    case OPERATION_OPTIONS.FEW_NETWORKS:
      return {
        typeDescription: ACCOUNT_TYPES.ENTREPRENEUR,
        startProfilesCount: 1,
        onboardingStep: ONBOARDING_STEPS.OPERATION_COMPLETE,
        nextScreen: UI_SCREENS.CONTACT,
      };
    case OPERATION_OPTIONS.AGENCY:
      return {
        typeDescription: ACCOUNT_TYPES.AGENCY_OR_PROFESSIONAL,
        startProfilesCount: undefined,
        onboardingStep: ONBOARDING_STEPS.OPERATION_CHOICE,
        nextScreen: UI_SCREENS.AGENCY_SCALE,
      };
    case OPERATION_OPTIONS.FRANCHISE:
      return {
        typeDescription: undefined,
        startProfilesCount: undefined,
        onboardingStep: ONBOARDING_STEPS.OPERATION_CHOICE,
        nextScreen: UI_SCREENS.FRANCHISE_SCOPE,
      };
    case OPERATION_OPTIONS.OTHER:
      return {
        typeDescription: ACCOUNT_TYPES.OUTROS,
        startProfilesCount: undefined,
        onboardingStep: ONBOARDING_STEPS.OPERATION_CHOICE,
        nextScreen: UI_SCREENS.OTHER_SCALE,
      };
    default:
      return null;
  }
};

export const resolveFranchiseScopeAnswer = (scopeKey) => {
  if (scopeKey === 'one') {
    return {
      typeDescription: ACCOUNT_TYPES.FRANCHISEE,
      startProfilesCount: 1,
      onboardingStep: ONBOARDING_STEPS.OPERATION_COMPLETE,
      nextScreen: UI_SCREENS.CONTACT,
    };
  }

  if (scopeKey === 'several') {
    return {
      typeDescription: ACCOUNT_TYPES.FRANCHISING,
      startProfilesCount: undefined,
      onboardingStep: ONBOARDING_STEPS.FRANCHISE_SCOPE,
      nextScreen: UI_SCREENS.FRANCHISE_SCALE,
    };
  }

  return null;
};

export const resolveScaleAnswer = (screen, rangeKey) => {
  const startProfilesCount = mapRangeToCount(rangeKey);
  if (startProfilesCount === undefined) {
    return null;
  }

  const typeByScreen = {
    [UI_SCREENS.AGENCY_SCALE]: ACCOUNT_TYPES.AGENCY_OR_PROFESSIONAL,
    [UI_SCREENS.FRANCHISE_SCALE]: ACCOUNT_TYPES.FRANCHISING,
    [UI_SCREENS.OTHER_SCALE]: ACCOUNT_TYPES.OUTROS,
  };

  const typeDescription = typeByScreen[screen];
  if (!typeDescription) {
    return null;
  }

  return {
    typeDescription,
    startProfilesCount,
    onboardingStep: ONBOARDING_STEPS.OPERATION_COMPLETE,
    nextScreen: UI_SCREENS.CONTACT,
  };
};

export const getUiScreenForStep = (onboardingStep, typeDescription) => {
  if (!onboardingStep || onboardingStep < ONBOARDING_STEPS.OPERATION_CHOICE) {
    return UI_SCREENS.OPERATION;
  }

  if (onboardingStep === ONBOARDING_STEPS.OPERATION_CHOICE) {
    if (typeDescription === ACCOUNT_TYPES.AGENCY_OR_PROFESSIONAL) {
      return UI_SCREENS.AGENCY_SCALE;
    }
    if (typeDescription === ACCOUNT_TYPES.OUTROS) {
      return UI_SCREENS.OTHER_SCALE;
    }
    return UI_SCREENS.OPERATION;
  }

  if (onboardingStep === ONBOARDING_STEPS.FRANCHISE_SCOPE) {
    if (typeDescription === ACCOUNT_TYPES.FRANCHISING) {
      return UI_SCREENS.FRANCHISE_SCALE;
    }
    return UI_SCREENS.FRANCHISE_SCOPE;
  }

  return UI_SCREENS.CONTACT;
};

/** Histórico mínimo de telas para back após resume. */
export const getScreenHistoryForUi = (uiScreen) => {
  switch (uiScreen) {
    case UI_SCREENS.AGENCY_SCALE:
    case UI_SCREENS.OTHER_SCALE:
    case UI_SCREENS.FRANCHISE_SCOPE:
      return [UI_SCREENS.OPERATION, uiScreen];
    case UI_SCREENS.FRANCHISE_SCALE:
      return [UI_SCREENS.OPERATION, UI_SCREENS.FRANCHISE_SCOPE, UI_SCREENS.FRANCHISE_SCALE];
    default:
      return [UI_SCREENS.OPERATION];
  }
};

export const isOnboardingCompleted = (onboardingStep) =>
  onboardingStep >= ONBOARDING_STEPS.COMPLETED;

/**
 * Query no dashboard (REACT_APP_FULL_URL / mlabs-home) para abrir o modal
 * ConnectionSteps do @engenharia/mlabs-connect-client (“Conecte o Instagram”).
 * Contrato com mlabs-home: NÃO redirecionar a /channels/instagram/login (OAuth).
 */
export const OPEN_INSTAGRAM_CONNECT_QUERY = 'open_instagram_connect=1';

const ONE_PROFILE_INSTAGRAM_TYPES = [
  ACCOUNT_TYPES.ENTREPRENEUR,
  ACCOUNT_TYPES.FRANCHISEE,
  ACCOUNT_TYPES.OUTROS,
];

/**
 * Ramos 1-perfil (SOC-4873 / SOC-4877): Poucas redes, Franqueado UMA, Outro=1.
 * Agência com count 1 NÃO entra (dashboard limpo, sem query).
 */
export const shouldOpenInstagramSimplifiedConnect = ({
  typeDescription,
  startProfilesCount,
} = {}) =>
  Number(startProfilesCount) === 1 && ONE_PROFILE_INSTAGRAM_TYPES.includes(typeDescription);

export const resolvePostOnboardingRedirectUrl = ({
  typeDescription,
  startProfilesCount,
  fullUrl = process.env.REACT_APP_FULL_URL,
} = {}) => {
  const base = String(fullUrl || '').replace(/\/$/, '');

  if (shouldOpenInstagramSimplifiedConnect({ typeDescription, startProfilesCount })) {
    return `${base}?${OPEN_INSTAGRAM_CONNECT_QUERY}`;
  }

  return base;
};
